import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import {
    HasParentProductId,
    ParentProductIdContext,
    withParentProductId,
} from "@insite/client-framework/Components/ParentProductContext";
import { HasProduct, ProductContext, withProduct } from "@insite/client-framework/Components/ProductContext";
import { ConfigurationType, getProductRealTimePrice } from "@insite/client-framework/Services/ProductServiceV2";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getVariantChildrenDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityDto,
    AvailabilityMessageType,
    ProductInventoryDto,
    ProductModel,
    ProductSettingsModel,
    RealTimeInventoryModel,
    RealTimePricingModel,
} from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import Button from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, safeColor } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import ProductAvailibiltyTable from "@tSurya/Components/ProductAvailibiltyTable";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import color from "color";
import * as React from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps> & HasParentProductId;

const mapStateToProps = (state: ApplicationState, ownProps: HasParentProductId) => ({
    childVarients: getVariantChildrenDataView(state, ownProps.parentProductId).value,
    productInfo: state.pages.productDetails.productInfosById,
    productSettings: getSettingsCollection(state).productSettings,
    isAuthenticated: state.context.session.isAuthenticated,
});

const mapDispatchToProps = {
    loadRealTimePricing,
    loadRealTimeInventory,
};

export interface ProductDetailsAvailabilityStyles {
    availability?: ProductAvailabilityStyles;
    availabilityContainer?: InjectableCss;
    availabilityByWarehouseLink?: LinkPresentationProps;
    availabilityByWarehouseModal?: ModalPresentationProps;
    inventoryMessage?: InjectableCss;
    messageText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    inStockStyle?: TypographyPresentationProps;
}

export const availabilityStyles: ProductDetailsAvailabilityStyles = {
    availabilityContainer: {
        css: css``,
    },
    availabilityByWarehouseLink: {
        css: css`
            span {
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-style: normal;
                font-weight: ${TYPOGRAPHY.weight.bold};
                font-size: ${TYPOGRAPHY.size.small};
                line-height: ${TYPOGRAPHY.lineHeight.base};
                color: ${getColor("text.link")};
                white-space: nowrap;
            }
        `,
    },
    availabilityByWarehouseModal: {
        sizeVariant: "large",
        cssOverrides: {
            modalBody: css`
            ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        null,
                        css`max-width: 1200px !important;`,
                        css`max-width: 1200px !important;`,
                    ])}
            `,
            modalContent: css`
                padding: 0;
            `,
            modalTitle: css`
                padding: 10px 10px 10px 20px;
                background-color: ${getColor("common.backgroundContrast")};
                border-bottom: 0;
                > h4 {
                    color: ${getColor("text.main")};
                }
                button {
                    border-radius: 0;
                    border: 1px solid ${getColor("primary")} !important;
                    svg {
                        height: 20px;
                    }
                }
            `,
            headlineTypography: css`
                font-size: 20px;
                margin-bottom: 0;
                font-weight: bold;
            `,
        },
    },
    messageText: {
        css: css`
            width: 100%;
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.bold};
            font-size: ${TYPOGRAPHY.size.base};
            line-height: ${TYPOGRAPHY.lineHeight.xbase};
            color: ${getColor("text.main")};
            cursor: auto;
            white-space: nowrap;
            ${wrapInContainerStyles}
        `,
    },
    inStockStyle: {
        css: css`
            width: 100%;
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.bold};
            font-size: ${TYPOGRAPHY.size.base};
            line-height: ${TYPOGRAPHY.lineHeight.xbase};
            color: ${getColor("text.main")};
            cursor: auto;
            white-space: nowrap;
            ${wrapInContainerStyles}
        `,
    },
    realTimeText: {
        weight: "bold",
        css: css`
            display: inline-block;
            text-align: left;
            min-width: 25px;

            &::after {
                overflow: hidden;
                display: inline-block;
                vertical-align: bottom;
                animation: ellipsis steps(4, end) 900ms infinite 1s;
                content: "\\2026";
                width: 0;
            }

            @keyframes ellipsis {
                to {
                    width: 1.25em;
                }
            }
        `,
    },
};
const InventoryMessage = styled.div<{ color: string }>`
    display: flex;
    align-items: center;
    padding: 5px;
    border-radius: 5px;
    background-color: ${props => safeColor(resolveColor(props.color, props.theme)).rgb().alpha(0.2).string()};
    ${IconWrapper} {
        margin-right: 10px;
    }
`;

const styles = availabilityStyles;

const getAvailabilityColor = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return "common";
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return "danger";
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return "warning";
    }

    return "success";
};

const ProductDetailsAvailability = ({
    product,
    childVarients,
    productInfo,
    productSettings,
    isAuthenticated,
}: Props) => {
    const { inventory, unitOfMeasure, failedToLoadInventory } = productInfo;

    const [modalIsOpen, setModalIsOpen] = React.useState(false);

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const viewAvailabilityByWarehouseClickHandler = () => {
        setModalIsOpen(true);
    };

    const getFirstUpcomingDate = (inventory: ProductInventoryDto): string =>{
        let msg = "";
        if (inventory?.additionalResults?.q30Msg && inventory.additionalResults.q30Msg !== "") {
            msg = inventory.additionalResults.q30Msg;
        } else if (inventory?.additionalResults?.q60Msg && inventory.additionalResults.q60Msg !== "") {
            msg = inventory.additionalResults.q60Msg;
        } else if (inventory?.additionalResults?.q90Msg && inventory.additionalResults.q90Msg !== "") {
            msg = inventory.additionalResults.q90Msg;
        }else{
            return availability?.message ?? ""
        }
        const cleanedMsg: string = msg.replace(/\(\d+\+?\)/g, '').trim();
      
        const dateStrings: string[] = cleanedMsg.split(';').map(date => date.trim());
       
        const sortedDates: string[] = dateStrings.sort((firstStr, secondStr) => {
            const firstDate = new Date(firstStr);
            const secondDate = new Date(secondStr);
            return firstDate.getTime() - secondDate.getTime();
        });
        const latestDate = sortedDates[0];
        return `Expected ${latestDate}`;
    }

    const productIDs = childVarients?.map(item => item.id);
    const availability =
        inventory?.inventoryAvailabilityDtos?.find(
            o => o.unitOfMeasure.toLowerCase() === (unitOfMeasure?.toLowerCase() || ""),
        )?.availability || undefined;
    const color = getAvailabilityColor(productSettings, availability);

    let inventoryTextComponent = <Typography {...styles.realTimeText} />;
    if (availability?.messageType !== AvailabilityMessageType.NoMessage && availability?.message?.length) {

        const AvailabilityMessage = availability.messageType === AvailabilityMessageType.OutOfStock && inventory ? getFirstUpcomingDate(inventory) : availability.message;
        inventoryTextComponent = (
            <InventoryMessage color={color} {...styles.inventoryMessage}>
                {/* <Icon color={color} src={iconSrc} /> */}
                <Typography
                    // eslint-disable-next-line no-constant-condition
                    {...(availability.messageType === AvailabilityMessageType.OutOfStock
                        ? { ...styles.messageText }
                        : { ...styles.inStockStyle })}
                    data-test-selector="availabilityMessage"
                >
                    {AvailabilityMessage}
                </Typography>
            </InventoryMessage>
        );
    } else if (availability) {
        inventoryTextComponent = <></>;
    }
    const parentProductId = React.useContext(ParentProductIdContext);
    const productContext = React.useContext(ProductContext);

    return (
        <StyledWrapper
            css={css`
                display: flex;
                margin-bottom: 17.33px;
                div[class^="InventoryMessage"] {
                    background-color: transparent;
                    padding: 0;
                    margin-right: 18px;
                    ${({ theme }: { theme: BaseTheme }) => 
                        breakpointMediaQueries(theme, [
                css`
                                margin-right: 7px;
                            `,
                css`
                                margin-right: 7px;
                            `,
                css`
                                margin-right: 7px;
                            `,
                null,
                null,
            ])}
                }
            `}
        >
            {parentProductId !== productContext.product.id && inventoryTextComponent}
            <Button variant="secondary"
                onClick={viewAvailabilityByWarehouseClickHandler}
            >
                {translate("See Availability")}
            </Button>
            {/* <Link {...styles.availabilityByWarehouseLink} onClick={viewAvailabilityByWarehouseClickHandler}>
        See Availibility
    </Link> */}
            <Modal
                {...styles.availabilityByWarehouseModal}
                headline={translate("Product Availability")}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
            >
                <ProductAvailibiltyTable childVarients={childVarients} />
            </Modal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withParentProductId(connect(mapStateToProps)(withProduct(ProductDetailsAvailability))),
    definition: {
        displayName: "Availability",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
